import React, { useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CircularProgress, FormControlLabel, Paper, Stack, Switch,
  TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useParams, useSearchParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { addDays, time, toTimeOnly, toggleIn, today } from '../labels';
import { NeedsHousehold, PageHeader, useConfirm, useHousehold, useLoadInto } from '../common';
import { MemberChips, useGoBack } from '../editing';

/** Create (/casa/eventos/novo?data=) or edit (/casa/eventos/:eventId) an event for anyone in the household, children included. */
const CalendarEventEditPage: React.FC = () => {
  const { eventId } = useParams();
  const [search] = useSearchParams();
  const { members } = useHousehold();
  const goBack = useGoBack('/casa/semana');
  const [confirm, confirmDialog] = useConfirm();
  const isNew = !eventId;
  const initialDate = search.get('data') ?? today();

  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState<T.Guid[]>([]);
  const [date, setDate] = useState<T.DateOnly>(initialDate);
  const [allDay, setAllDay] = useState(true);
  const [start, setStart] = useState('10:00');
  const [end, setEnd] = useState('11:00');
  const [severalDays, setSeveralDays] = useState(false);
  const [endDate, setEndDate] = useState<T.DateOnly>(addDays(initialDate, 1));
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  /** Read from a linked calendar: only "De quem é" can change here. */
  const [feedName, setFeedName] = useState<string | null>(null);
  const readOnly = feedName !== null;

  const { loading, error, setError } = useLoadInto(eventId ? () => api.getCalendarEvent(eventId) : null, event => {
    if (!event.isEditable) setFeedName(event.feedName ?? '');
    setTitle(event.title);
    setSelected(event.memberPublicIds);
    setDate(event.date);
    setAllDay(event.isAllDay);
    if (event.startTime) setStart(time(event.startTime));
    if (event.endTime) setEnd(time(event.endTime));
    setSeveralDays(event.endDate !== event.date);
    setEndDate(event.endDate);
    setLocation(event.location ?? '');
    setNotes(event.notes ?? '');
  }, [eventId]);

  const toggle = (id: T.Guid) =>
    setSelected(current => toggleIn(current, id));

  const saveMembers = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.setCalendarEventMembers(eventId!, members.filter(m => selected.indexOf(m.publicId) >= 0).map(m => m.publicId));
      goBack();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (readOnly) { await saveMembers(); return; }
    const name = title.trim();
    if (!name) { setError('Indica o evento.'); return; }
    const request: T.SaveCalendarEventRequest = {
      title: name,
      date,
      endDate: severalDays ? endDate : null,
      startTime: allDay ? null : toTimeOnly(start),
      endTime: allDay ? null : toTimeOnly(end),
      location: location.trim() || null,
      notes: notes.trim() || null,
      // In the household's order, only people still in it
      memberPublicIds: members.filter(m => selected.indexOf(m.publicId) >= 0).map(m => m.publicId),
    };
    setBusy(true);
    setError(null);
    try {
      if (eventId) await api.updateCalendarEvent(eventId, request); else await api.createCalendarEvent(request);
      goBack();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!eventId) return;
    if (!await confirm({ title: 'Apagar', text: `Apagar o evento "${title}"?`, confirmText: 'Apagar', danger: true })) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteCalendarEvent(eventId);
      goBack();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const detailsSummary = [location.trim(), notes.trim()].filter(Boolean).join(' · ') || 'Nenhum';

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 640, pb: 10 }}>
      <PageHeader title={isNew ? 'Novo evento' : readOnly ? 'De quem é' : 'Editar evento'} />
      <Stack spacing={2}>
        {readOnly && (
          <Alert severity="info">
            Vem do calendário "{feedName}": o resto muda-se lá. Aqui escolhes de quem é, e fica assim nas próximas leituras.
          </Alert>
        )}
        <TextField
          label="Evento" placeholder="Ex.: Festa de Natal da creche" value={title} autoFocus={isNew} disabled={readOnly}
          onChange={e => setTitle(e.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }}
        />
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>De quem é</Typography>
          <MemberChips members={members} selected={selected} onToggle={toggle} />
          <Typography variant="caption" color="text.secondary">Sem ninguém escolhido, é da casa toda.</Typography>
        </Box>
        <TextField
          type="date" label="Dia" value={date} disabled={readOnly}
          onChange={e => setDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }}
        />
        <Box>
          <FormControlLabel control={<Switch checked={allDay} disabled={readOnly} onChange={e => setAllDay(e.target.checked)} />} label="Dia todo" />
          {!allDay && (
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <TextField
                type="time" label="Começa" value={start} fullWidth disabled={readOnly}
                onChange={e => setStart(e.target.value)} slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                type="time" label="Acaba" value={end} fullWidth disabled={readOnly}
                onChange={e => setEnd(e.target.value)} slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
          )}
        </Box>
        <Box>
          <FormControlLabel control={<Switch checked={severalDays} disabled={readOnly} onChange={e => setSeveralDays(e.target.checked)} />} label="Vários dias" />
          {severalDays && (
            <TextField
              type="date" label="Último dia" value={endDate} fullWidth disabled={readOnly} sx={{ mt: 1 }}
              onChange={e => setEndDate(e.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: date } }}
            />
          )}
        </Box>

        <Accordion expanded={detailsOpen} onChange={(_, open) => setDetailsOpen(open)} disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600 }}>Local e notas</Typography>
              {!detailsOpen && <Typography variant="caption" color="text.secondary" noWrap component="div">{detailsSummary}</Typography>}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField label="Local" placeholder="Opcional" value={location} disabled={readOnly} onChange={e => setLocation(e.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }} />
              <TextField
                multiline minRows={2} label="Notas" placeholder="Ex.: levar lanche e roupa de festa" value={notes} disabled={readOnly}
                onChange={e => setNotes(e.target.value)}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {error && <Alert severity="error">{error}</Alert>}
        {!isNew && !readOnly && <Button variant="outlined" color="error" onClick={remove} disabled={busy}>Apagar evento</Button>}
      </Stack>

      {/* Always in reach */}
      <Paper
        elevation={6}
        sx={{ position: 'sticky', bottom: { xs: 64, md: 8 }, mt: 2, p: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', zIndex: 2 }}
      >
        <Button onClick={goBack} disabled={busy}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy}>Guardar</Button>
      </Paper>
      {confirmDialog}
    </Box>
  );
};

const CalendarEventEditRoute: React.FC = () => <NeedsHousehold><CalendarEventEditPage /></NeedsHousehold>;
export default CalendarEventEditRoute;
